import { FormProvider } from '@neo/blocks/forms/form-provider'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Button } from '@neo/primitives/button'
import { Dialog, DialogContent } from '@neo/primitives/dialog'
import { type ReactNode, useMemo } from 'react'
import { useNavigate } from 'router-v6'
import { REGIONS } from 'v2/configs/constants'
import { useCreateOrganizationMutation } from 'v2/pages/auth/queries'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { successToast } from 'v2/utils/toasts'
import {
  CREATE_ORGANIZATION_DEFAULT_VALUES,
  type CreateOrganizationFormValues,
  getCreateOrganizationFields,
} from './form-config'

interface CreateOrganizationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingOrgNames?: string[]
  /** Pre-select the data region (defaults to US). */
  defaultRegion?: string
  // Free-trial accounts get one org per data region. When set, the region select
  // is locked to the region the user hasn't used yet — or creation is blocked when
  // both regions are already used — with an upgrade nudge. See SER-5737.
  freeTrialRegionLock?: {
    atLimit: boolean
    lockedRegion: string
    currentRegion: string
  }
}

const regionLabel = (region: string) => (region === REGIONS.IN ? 'India' : 'United States')

const AT_LIMIT_REASON =
  'Free trial includes one organization per data region, and both are already in use. Upgrade to create more.'

// Inline "upgrade your plan" CTA used within the free-trial note. Neo Button
// (link variant) stripped to inline sizing so it sits in a sentence.
const UpgradeLink = ({ onUpgrade, children }: { onUpgrade: () => void; children: ReactNode }) => (
  <Button
    variant="link"
    onClick={onUpgrade}
    className="h-auto p-0 underline"
    data-testid="button-upgrade-plan"
  >
    {children}
  </Button>
)

const FreeTrialRegionNote = ({
  lock,
  onUpgrade,
}: {
  lock: NonNullable<CreateOrganizationModalProps['freeTrialRegionLock']>
  onUpgrade: () => void
}) => (
  <div
    className="mb-4 rounded-md bg-hv1 px-3 py-2 text-xs-400 text-tertiary"
    data-testid="create-org-free-trial-note"
  >
    {lock.atLimit ? (
      <>
        Your free trial includes one organization per data region, and both are already in use.{' '}
        <UpgradeLink onUpgrade={onUpgrade}>Upgrade your plan</UpgradeLink> to create more
        organizations.
      </>
    ) : (
      <>
        Free trial includes one organization per data region. This organization will be created in{' '}
        <span className="text-s-500">{regionLabel(lock.lockedRegion)}</span>. To add another in{' '}
        {regionLabel(lock.currentRegion)},{' '}
        <UpgradeLink onUpgrade={onUpgrade}>upgrade your plan</UpgradeLink>.
      </>
    )}
  </div>
)

export const CreateOrganizationModal = ({
  open,
  onOpenChange,
  existingOrgNames = [],
  defaultRegion,
  freeTrialRegionLock,
}: CreateOrganizationModalProps) => {
  const navigate = useNavigate()
  const createOrganizationMutation = useCreateOrganizationMutation()

  const atLimit = Boolean(freeTrialRegionLock?.atLimit)
  const lockedRegion =
    freeTrialRegionLock && !atLimit ? freeTrialRegionLock.lockedRegion : undefined

  const formFields = useMemo(
    () => getCreateOrganizationFields(existingOrgNames, { lockedRegion }),
    [existingOrgNames, lockedRegion],
  )

  // Stable reference: GenericForm resets whenever defaultValues changes identity.
  const defaultValues = useMemo(
    () => ({
      ...CREATE_ORGANIZATION_DEFAULT_VALUES,
      region: lockedRegion ?? defaultRegion ?? REGIONS.US,
    }),
    [lockedRegion, defaultRegion],
  )

  const handleClose = () => {
    onOpenChange(false)
  }

  const handleUpgrade = () => {
    onOpenChange(false)
    navigate('/billing/plans-and-pricing')
  }

  const onSubmit = (data: CreateOrganizationFormValues) => {
    createOrganizationMutation.mutate(
      {
        organization_name: data.organization_name,
        region: data.region,
        invitee_emails: data.invite_members?.length ? data.invite_members : undefined,
      },
      // Errors are toasted by useCreateOrganizationMutation with the backend's reason.
      {
        onSuccess: () =>
          successToast({
            title: 'Organization created successfully',
            description: 'You can now start using your new organization.',
            callback: handleClose,
          }),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] p-0 overflow-hidden" showCloseButton={false}>
        <div className="flex">
          {/* Left side - Form */}
          <div className="flex-1 flex flex-col justify-between p-6">
            <h2 className="text-h4-500 mb-6">Create new organization</h2>
            {freeTrialRegionLock && (
              <FreeTrialRegionNote lock={freeTrialRegionLock} onUpgrade={handleUpgrade} />
            )}
            <FormProvider>
              <GenericForm<CreateOrganizationFormValues>
                id="create-organization-form"
                defaultValues={defaultValues}
                fields={formFields}
                className="gap-6"
                onSubmit={onSubmit}
                includeSubmitButton={false}
              />
              <div className="flex justify-end mt-6">
                <FormSubmitButton
                  formId="create-organization-form"
                  data-testid="button-create-organization"
                  loading={createOrganizationMutation.isPending}
                  disabled={atLimit}
                  disabledReason={atLimit ? AT_LIMIT_REASON : undefined}
                  disableOnInvalid
                >
                  Create Organization
                </FormSubmitButton>
              </div>
            </FormProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
